<template>
    <div v-if="interims.length">
        <draggable
            v-bind="dragOptions"
            v-model="interims"
            item-key="id"
            handle=".draggable"
            class="space-y-12 min-h-64"
            :class="{'border border-dashed rounded border-primary-20 w-full': checkInterimSection, 'space-y-0': hideSpacer}"
            :move="checkMove"
            @end="updateOrder"
            @start="start"
        >
            <template #item="{ element }">
                <div>
                    <div v-if="element.section_id === sectionId" @mousedown="currentInterim = element">
                        <interim-list
                            :interim="element"
                            @edit="startEditingInterim"
                            @delete="startDeletingInterim"
                        />
                    </div>
                </div>
            </template>
        </draggable>

        <!-- Popup add/edit Interim -->
        <add-interim
            v-if="editingInterim"
            :active-item="value"
            :interim="editingInterim"
            @close="stopEditingInterim"
        />

        <!-- Popup delete Interim -->
        <popup-modal-confirm
            v-if="deletingInterim"
            :message="$t('interims.confirm_deletion')"
            show-exclamation
            :working="performingDeleteRequest"
            @close="stopDeletingInterim"
            @confirm="confirmInterimDeletion"
        />
    </div>
</template>

<script>
import InterimList from './InterimList';
import draggable from 'vuedraggable';
import Interim from '../models/Interim';
import * as interims from '../services/InterimService';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import {mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useNotificationsStore} from '../state/modules/notifications';
import AddInterim from './ui/AddInterim';
import PopupModalConfirm from './ui/PopupModalConfirm';

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    components: {PopupModalConfirm, AddInterim, InterimList, draggable},
    data() {
        return {
            currentInterim: null,
            editingInterim: false,
            deletingInterim: false,
            performingDeleteRequest: false,
        };
    },
    props: {
        drag: Boolean,
        value: {
            default: 'interim',
        },
        sectionId: {
            default: null,
        },
        highlight: null,
    },
    computed: {
        dragOptions() {
            return {
                animation: 200,
                group: 'interims',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
        interims: {
            get() {
                return useRepo(Interim)
                    .with('matrixValues', (q) => {
                        q.with('type');
                    })
                    .orderBy('order')
                    .where('type', this.value)
                    .get();
            },
            set(interims) {
                this.resort(interims);
            },
        },
        checkInterimSection() {
            if (!this.sectionId) return false;

            // Check if you need to show the empty section placeholder
            // When there are no items, or we're dragging the final item out of the section

            let items = this.sectionItems;
            let count = items.length;
            return count === 0 ||
                (this.drag &&
                    count === 1 &&
                    this.currentInterim &&
                    this.currentInterim.id == items[0].id);
        },
        hideSpacer() {
            return this.sectionItems.length === 0 && this.drag;
        },
        sectionItems() {
            return this.interims.filter(x => x.section_id === this.sectionId);
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        startEditingInterim(interim) {
            this.editingInterim = interim;
        },
        stopEditingInterim() {
            this.editingInterim = false;
        },
        startDeletingInterim(interim) {
            this.deletingInterim = interim;
        },
        stopDeletingInterim() {
            this.deletingInterim = false;
        },
        confirmInterimDeletion() {
            this.performingDeleteRequest = true;

            interims.destroy(this.deletingInterim.id)
                .then(() => {
                    return useRepo(Interim).destroy(this.deletingInterim.id);
                })
                .finally(() => {
                    this.performingDeleteRequest = false;
                    this.deletingInterim = false;
                });
        },
        resort(sortedInterims) {
            useRepo(Interim).save(
                sortedInterims
                    .map((interim, index) => ({id: interim.id, order: index}))
            );
        },
        updateOrder(evt) {
            let currentInterim = this.currentInterim;

            //Check whether you drag an interim in a section
            let sectionFrom = evt.from.closest('div[id^="section-"]');
            let sectionTo = evt.to.closest('div[id^="section-"]');

            let toSectionId = 0;

            if ((sectionFrom && sectionTo) && sectionFrom.id !== sectionTo.id) {
                //You drag an interim from a section to a new section
                toSectionId = parseInt(sectionTo.id.replace('section-', ''));
            } else {
                if (!sectionFrom && (sectionTo && sectionTo.id)) {
                    //!sectionFrom means from is null and you drag an interim that is not from a section to a section
                    toSectionId = parseInt(sectionTo.id.replace('section-', ''));
                }
                if ((toSectionId === 0) && (sectionFrom && sectionFrom.id) && !sectionTo) {
                    //When you drag an interim from a section to outside the list set sectionId to null
                    toSectionId = null;
                }
            }

            if (toSectionId !== 0) {
                useRepo(Interim)
                    .whereId(currentInterim.id)
                    .update({section_id: toSectionId});

                interims.update(currentInterim.id, {
                        data: {
                            attributes: {section_id: toSectionId},
                        },
                    },
                ).then(() => {
                    if (evt.newDraggableIndex === currentInterim.order) {
                        this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('interims.drop_successful'),
                        });
                    }
                });
            }

            //Check for order change
            if (evt.newDraggableIndex === currentInterim.order) {
                this.currentInterim = null;
                this.$emit('end');
                return;
            }

            interims.update(currentInterim.id, {
                    data: {
                        attributes: {order: evt.newDraggableIndex},
                    },
                },
            ).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: 'interim'}),
                });
            });

            this.currentInterim = null;
            this.$emit('end');
        },
        start() {
            //Start drag of element to set drag true
            this.$emit('start');
        },
        checkMove(evt) {
            //Start drag on element and check if you drag it into empty section to enable highlight prop
            this.$emit('move', evt);
        },
    },
};
</script>
